// Estimation de hauteur par la méthode de McLeod (McLeod Pitch Method) :
// fonction de différence carrée normalisée (NSDF), puis choix du premier pic
// suffisamment haut par rapport au pic maximal. Cette règle privilégie la période
// fondamentale et limite les erreurs d'octave vers le haut tant que le fondamental
// n'est pas quasi absent du signal.

export interface PitchEstimate {
  /** Fréquence estimée en Hz. */
  frequency: number;
  /** Clarté 0 à 1 : hauteur du pic NSDF retenu. */
  clarity: number;
  /** Période estimée en échantillons (interpolée). */
  period: number;
}

export interface MpmOptions {
  minFrequency: number;
  maxFrequency: number;
  /** Un pic est retenu s'il atteint threshold × (pic maximal). Typiquement 0,8 à 0,95. */
  threshold: number;
}

interface Peak {
  lag: number;
  value: number;
}

/**
 * Calcule la NSDF pour les retards 0..maxLag dans `out` (longueur ≥ maxLag + 1).
 * Coût : n × maxLag opérations.
 */
export function computeNsdf(buffer: Float32Array, maxLag: number, out: Float32Array): void {
  const n = buffer.length;
  for (let tau = 0; tau <= maxLag; tau++) {
    let acf = 0;
    let m = 0;
    const limit = n - tau;
    for (let i = 0; i < limit; i++) {
      const a = buffer[i];
      const b = buffer[i + tau];
      acf += a * b;
      m += a * a + b * b;
    }
    out[tau] = m > 0 ? (2 * acf) / m : 0;
  }
}

function pickPeaks(nsdf: Float32Array, minLag: number, maxLag: number): Peak[] {
  const peaks: Peak[] = [];
  // Saute le lobe initial (autour de tau = 0) jusqu'au premier passage sous zéro.
  let tau = 1;
  while (tau <= maxLag && nsdf[tau] > 0) tau++;
  // Puis, pour chaque lobe positif, garde son maximum (interpolé par une parabole).
  while (tau < maxLag) {
    while (tau < maxLag && nsdf[tau] <= 0) tau++;
    let bestIndex = -1;
    let bestValue = -Infinity;
    while (tau < maxLag && nsdf[tau] > 0) {
      if (nsdf[tau] > bestValue) {
        bestValue = nsdf[tau];
        bestIndex = tau;
      }
      tau++;
    }
    if (bestIndex > 0 && bestIndex < maxLag) {
      const a = nsdf[bestIndex - 1];
      const b = nsdf[bestIndex];
      const c = nsdf[bestIndex + 1];
      // Interpolation parabolique, seulement si le sommet est franc (parabole concave, décalage < 1 échantillon).
      let lag = bestIndex;
      let value = b;
      const denom = a - 2 * b + c;
      if (denom < -1e-9) {
        const shift = (0.5 * (a - c)) / denom;
        if (Math.abs(shift) <= 1) {
          lag = bestIndex + shift;
          value = b - 0.25 * (a - c) * shift;
        }
      }
      value = Math.min(1, value);
      if (lag >= minLag) peaks.push({ lag, value });
    }
  }
  return peaks;
}

/**
 * Estime la hauteur d'une fenêtre de signal. Renvoie null si aucun pic n'est trouvé.
 * `scratch` permet de réutiliser un tampon de longueur ≥ ceil(sampleRate / minFrequency) + 1.
 */
export function estimatePitch(
  buffer: Float32Array,
  sampleRate: number,
  options: MpmOptions,
  scratch?: Float32Array,
): PitchEstimate | null {
  const n = buffer.length;
  const minLag = Math.max(2, Math.floor(sampleRate / options.maxFrequency));
  const maxLag = Math.min(n - 2, Math.ceil(sampleRate / options.minFrequency));
  if (maxLag <= minLag) return null;
  const nsdf = scratch && scratch.length >= maxLag + 1 ? scratch : new Float32Array(maxLag + 1);
  computeNsdf(buffer, maxLag, nsdf);
  const peaks = pickPeaks(nsdf, minLag, maxLag);
  if (peaks.length === 0) return null;
  let maxValue = -Infinity;
  for (const p of peaks) if (p.value > maxValue) maxValue = p.value;
  if (maxValue <= 0) return null;
  const cutoff = options.threshold * maxValue;
  const chosen = peaks.find((p) => p.value >= cutoff) ?? peaks[0];
  return { frequency: sampleRate / chosen.lag, clarity: Math.max(0, Math.min(1, chosen.value)), period: chosen.lag };
}
